import type * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import { COLLECTION_TITLE_MAX_LENGTH } from '@gitbook/api-client';
import {
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient } from '../../state';

type RenameCollectionFormInputs = {
    title: string;
};

interface RenameCollectionDialogProps {
    collection: api.Collection;
}

/**
 * Modal to rename a collection.
 */
export function RenameCollectionDialog(props: RenameCollectionDialogProps): React.ReactElement {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Rename collection</Heading>
            </DialogHeader>
            <RenameCollectionForm {...props} />
        </Dialog>
    );
}

function RenameCollectionForm(props: RenameCollectionDialogProps) {
    const { collection } = props;

    const [isPending, startDialogAction] = useDialogAction();

    const form = useForm<RenameCollectionFormInputs>({
        values: {
            title: collection.title,
        },
        disabled: isPending,
    });

    const onSubmit: SubmitHandler<RenameCollectionFormInputs> = async ({ title }) => {
        await startDialogAction(async ({ close }) => {
            await apiClient.collections.updateCollectionById(collection.id, { title });
            close();
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <TextField
                    name="title"
                    required
                    minLength={1}
                    maxLength={COLLECTION_TITLE_MAX_LENGTH}
                >
                    <Label>Title</Label>
                    <TextInput autoFocus placeholder="My spaces" disabled={isPending} />
                    <FieldError />
                </TextField>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Rename" />
            </DialogFooter>
        </Form>
    );
}
